import { FC, PropsWithChildren } from "react";
import LoadingIndicator from "~/components/LoadingIndicator";

export const Wrapper: FC<PropsWithChildren<{ isEmpty?: boolean }>> = ({
  children,
  isEmpty,
}) => (
  <div
    className={`relative w-full max-w-[650px] ${isEmpty ? "min-h-[180px]" : ""}`}
  >
    {children}
  </div>
);

export const Header: FC<PropsWithChildren> = ({ children }) => (
  <div className="mx-auto grid w-full grid-flow-row-dense grid-cols-5 grid-rows-[max-content_1fr] border border-b-0 border-solid border-white200 bg-white p-2 font-bold">
    {children}
  </div>
);

export const Heading: FC<PropsWithChildren> = ({ children }) => (
  <h3 className="col-span-3 col-start-1 text-[1.25rem] leading-[1] text-blue900 xsm:col-span-2">
    {children}
  </h3>
);

export const TotalLabel: FC<PropsWithChildren> = ({ children }) => (
  <p className="space-between col-span-3 col-start-1 row-span-1 row-start-2 flex text-[13px] text-gray300 xsm:col-span-3">
    {children}
  </p>
);

export const TotalValue: FC<PropsWithChildren> = ({ children }) => (
  <span
    data-testid="bulk-pricing__current-total"
    className="m-0 text-[13px] text-blue900"
  >
    {children}
  </span>
);

export const Color: FC<PropsWithChildren> = ({ children }) => (
  <div className="col-span-2 col-start-4 row-span-1 row-start-1 flex justify-end gap-2 xsm:col-span-3 xsm:col-start-3">
    {children}
  </div>
);

export const ColorLabel: FC<PropsWithChildren> = ({ children }) => (
  <span className="text-[1.125rem] leading-[1] text-black">{children}</span>
);

export const ColorValue: FC<PropsWithChildren> = ({ children }) => (
  <span
    data-testid="bulk-pricing__color"
    className="hidden text-right text-[1.125rem] leading-[1] text-black xsm:inline-block"
  >
    {children}
  </span>
);

export const Status: FC<PropsWithChildren> = ({ children }) => (
  <span
    data-testid="bulk-pricing__status"
    className="col-span-2 col-start-4 row-start-2 text-right text-[13px] text-gray300"
  >
    {children}
  </span>
);

export const Table: FC<PropsWithChildren> = ({ children }) => (
  <table
    cellSpacing="0"
    className="mx-auto my-0 block w-full overflow-x-scroll border border-t-0 border-solid border-white200 bg-white p-0 font-bold text-black sm:table"
    aria-label="Bulk Pricing Guide"
  >
    {children}
  </table>
);

export const TableHeader: FC<
  PropsWithChildren<{
    dataTestId?: string;
    isSelected?: boolean;
  }>
> = ({ children, isSelected, dataTestId }) => (
  <th
    data-testid={dataTestId}
    className={`text-nowrap px-[8px] py-[4px] text-center font-bold leading-[1.375] ${
      isSelected ? "bg-blue text-white" : "bg-white300"
    }`}
  >
    <div className="flex flex-col text-nowrap text-[13px]">{children}</div>
  </th>
);

export const TableHeaderText: FC<{
  dataTestId?: string;
  isFirst: boolean;
  isSelected: boolean;
  isLessThanSelected: boolean;
  text: string;
}> = ({ isFirst, isLessThanSelected, isSelected, text }) => {
  const _text = text === "MEMBER" ? "$0+" : text;
  if (isLessThanSelected) {
    return <span className={!isFirst ? "text-gray300" : ""}>{_text}</span>;
  }

  if (isSelected) {
    return (
      <>
        <span>YOUR PRICE</span>
        <span>{_text}</span>
      </>
    );
  }

  return (
    <>
      <span className="text-[11px]">Product Total</span>
      <span>{_text}</span>
    </>
  );
};

export const TableCell: FC<
  PropsWithChildren<{
    dataTestId?: string;
    isLessThanSelected: boolean;
    isSelected: boolean;
    isSignedIn: boolean;
  }>
> = ({ children, isSelected, isLessThanSelected, dataTestId }) => (
  <td
    data-testid={dataTestId}
    className={`text-nowrap px-[4px] text-center text-[13px] font-bold leading-[1.25rem] ${isSelected ? "bg-blue100 text-black" : ""} ${isLessThanSelected ? "text-[#CCCCCC]" : ""}`}
  >
    {children}
  </td>
);

const StateWrapper: FC<PropsWithChildren> = ({ children }) => (
  <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/80 transition-opacity">
    {children}
  </div>
);

const StateMessage: FC<PropsWithChildren> = ({ children }) => (
  <div className="mb-2 text-center text-lg uppercase tracking-wider text-blue900">
    {children}
  </div>
);

export const LoadingState: FC = () => (
  <StateWrapper>
    <StateMessage>CALCULATING YOUR BULK PRICING</StateMessage>
    <LoadingIndicator />
  </StateWrapper>
);
